import { productConfig } from '@shared/config';

export type VisitorAnalyticsEvent =
  'page_view' | 'page_leave' | 'interaction' | 'cta_click' | 'signup_start' | 'signup_complete';

interface Attribution {
  referrerHost?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
}

interface VisitorAnalyticsPayload extends Attribution {
  sessionId: string;
  event: VisitorAnalyticsEvent;
  path: string;
  durationMs?: number;
}

const SESSION_STORAGE_KEY = 'careerlaunch-visitor-session';
const INTERACTION_COUNT_KEY = 'careerlaunch-visitor-interactions';
const MAX_INTERACTIONS_PER_SESSION = 60;
let fallbackSessionId: string | null = null;

function createSessionId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `00000000-0000-4000-8000-${Math.random().toString(16).slice(2).padEnd(12, '0').slice(0, 12)}`;
}

function storage(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

export function visitorSessionId(): string {
  if (typeof window === 'undefined') return createSessionId();
  const sessionStorage = storage();
  const existing = sessionStorage?.getItem(SESSION_STORAGE_KEY);
  if (
    existing &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/iu.test(existing)
  ) {
    return existing;
  }
  fallbackSessionId ??= createSessionId();
  sessionStorage?.setItem(SESSION_STORAGE_KEY, fallbackSessionId);
  return fallbackSessionId;
}

function normalizedShortValue(value: string | null): string | undefined {
  const normalized = value?.trim().toLowerCase() ?? '';
  return /^[a-z0-9][a-z0-9_-]{0,63}$/u.test(normalized) ? normalized : undefined;
}

function normalizedHostname(value: string): string | undefined {
  const normalized = value
    .trim()
    .toLowerCase()
    .replace(/^www\./u, '');
  if (
    normalized.length === 0 ||
    normalized.length > 120 ||
    !/^(?=.{1,120}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/u.test(normalized)
  ) {
    return undefined;
  }
  return normalized;
}

function attribution(): Attribution {
  if (typeof window === 'undefined') return {};
  const params = new URLSearchParams(window.location.search);
  let referrerHost: string | undefined;
  try {
    const referrer = new URL(document.referrer);
    if (referrer.hostname !== window.location.hostname) {
      referrerHost = normalizedHostname(referrer.hostname);
    }
  } catch {
    // Direct traffic and privacy-restricted browsers leave this blank.
  }
  return {
    referrerHost,
    utmSource: normalizedShortValue(params.get('utm_source')),
    utmMedium: normalizedShortValue(params.get('utm_medium')),
    utmCampaign: normalizedShortValue(params.get('utm_campaign')),
  };
}

/** Keeps private claim/form IDs and URL queries out of first-party analytics. */
export function analyticsPath(pathname: string): string {
  if (!pathname.startsWith('/')) return '/';
  if (/^\/form\/[^/]+\/?$/u.test(pathname)) return '/form/[id]';
  if (/^\/claim\/[^/]+\/?$/u.test(pathname)) return '/claim/[token]';
  return pathname.slice(0, 512) || '/';
}

export function trackVisitorEvent(
  event: VisitorAnalyticsEvent,
  pathname: string,
  durationMs?: number,
): void {
  if (typeof window === 'undefined') return;
  const payload: VisitorAnalyticsPayload = {
    sessionId: visitorSessionId(),
    event,
    path: analyticsPath(pathname),
    ...attribution(),
  };
  if (durationMs !== undefined) payload.durationMs = Math.max(0, Math.min(durationMs, 1_800_000));

  void fetch('/api/analytics/event', {
    method: 'POST',
    credentials: 'same-origin',
    keepalive: true,
    headers: {
      'content-type': 'application/json',
      'x-requested-with': productConfig.brandName,
    },
    body: JSON.stringify(payload),
  }).catch(() => {
    // Analytics must never affect a visitor's workflow.
  });
}

export function canTrackInteraction(): boolean {
  if (typeof window === 'undefined') return false;
  const sessionStorage = storage();
  const count = Number.parseInt(sessionStorage?.getItem(INTERACTION_COUNT_KEY) ?? '0', 10) || 0;
  if (count >= MAX_INTERACTIONS_PER_SESSION) return false;
  sessionStorage?.setItem(INTERACTION_COUNT_KEY, String(count + 1));
  return true;
}
